import React from 'react'
import { motion } from 'framer-motion'
import { 
  ArrowUpRight, 
  Zap, 
  Gift, 
  Database, 
  BarChart3,
  ChevronDown
} from 'lucide-react'
import { API_ENDPOINTS } from '../../config/apiConfig'
import { useAPIRequestStore } from '../../stores/apiStore'

const APIInfoCards: React.FC = () => {
  const { setSelectedEndpoint } = useAPIRequestStore()

  const apiCards = [
    {
      id: 'offers',
      title: 'Moments API',
      description: 'Deliver relevant perks at key customer journey moments to boost loyalty and engagement',
      icon: Zap,
      color: 'from-blue-500 to-blue-600',
      features: ['Real-time targeting', 'User journey optimization', 'Engagement analytics']
    },
    {
      id: 'perkswall',
      title: 'Perkswall API',
      description: 'Create personalized reward experiences with curated offers and perks',
      icon: Gift,
      color: 'from-purple-500 to-purple-600',
      features: ['Personalized rewards', 'User preferences', 'Engagement tracking']
    },
    {
      id: 'catalog',
      title: 'Offer Catalog API',
      description: 'Browse and filter through available offers with comprehensive targeting options',
      icon: Database,
      color: 'from-green-500 to-green-600',
      features: ['Offer filtering', 'Multi-platform support', 'Geographic targeting']
    },
    {
      id: 'reporting',
      title: 'Reporting API',
      description: 'Access detailed analytics and performance metrics for your campaigns',
      icon: BarChart3,
      color: 'from-orange-500 to-orange-600',
      features: ['Performance metrics', 'Custom date ranges', 'Data aggregation']
    }
  ]

  const handleCardClick = (apiId: string) => {
    const endpoint = API_ENDPOINTS.find(ep => ep.id === apiId)
    if (endpoint) {
      setSelectedEndpoint(endpoint)
      // Scroll to API dropdown with smooth animation
      const dropdown = document.querySelector('[data-api-dropdown]')
      if (dropdown) {
        dropdown.scrollIntoView({ behavior: 'smooth', block: 'center' })
        // Add a subtle highlight effect
        dropdown.classList.add('ring-2', 'ring-blue-500', 'ring-opacity-50')
        setTimeout(() => {
          dropdown.classList.remove('ring-2', 'ring-blue-500', 'ring-opacity-50')
        }, 2000)
      }
    }
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  }

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 20,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  }

  const arrowVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: {
        delay: 1.0,
        duration: 0.6,
        ease: "easeOut"
      }
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-6 text-center">
        <motion.h2 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-2xl font-bold text-gray-900 dark:text-white mb-2"
        >
          Explore MomentScience APIs
        </motion.h2>
        <motion.p 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-gray-600 dark:text-gray-400"
        >
          Choose an API to start building powerful engagement experiences
        </motion.p>
      </div>

      {/* API Cards Grid */}
      <div className="flex-1 px-6 pb-6">
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full"
        >
          {apiCards.map((api) => {
            const IconComponent = api.icon
            return (
              <motion.div
                key={api.id}
                variants={cardVariants}
                whileHover={{ 
                  scale: 1.02,
                  transition: { duration: 0.2 }
                }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleCardClick(api.id)}
                className="group relative bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 cursor-pointer hover:shadow-lg transition-all duration-300 hover:border-gray-300 dark:hover:border-gray-600"
              >
                {/* Gradient Background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${api.color} opacity-0 group-hover:opacity-5 rounded-xl transition-opacity duration-300`} />
                
                {/* Icon */}
                <div className={`inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br ${api.color} rounded-lg mb-4`}>
                  <IconComponent className="w-6 h-6 text-white" />
                </div>

                {/* Content */}
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  {api.title}
                </h3>
                
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">
                  {api.description}
                </p>

                {/* Features */}
                <ul className="space-y-1 mb-4">
                  {api.features.map((feature, index) => (
                    <li key={index} className="text-xs text-gray-500 dark:text-gray-500 flex items-center">
                      <div className="w-1 h-1 bg-gray-400 rounded-full mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Action Arrow */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300">
                    Try API
                  </span>
                  <ArrowUpRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
              </motion.div>
            )
          })}
        </motion.div>

        {/* Animated Arrow pointing to dropdown */}
        <motion.div
          variants={arrowVariants}
          initial="hidden"
          animate="visible"
          className="flex items-center justify-center mt-8 space-x-3"
        >
          <motion.div
            animate={{ 
              y: [0, -8, 0],
              rotate: [0, -5, 5, 0]
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="flex items-center space-x-2 text-blue-600 dark:text-blue-400"
          >
            <span className="text-sm font-medium">Select an endpoint above to get started</span>
            <motion.div
              animate={{ x: [0, 5, 0] }}
              transition={{ 
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <ChevronDown className="w-5 h-5" />
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}

export default APIInfoCards
